import { useMemo } from "react"
import { map, chain, filter, find, omit, toLower, some, isNil } from "lodash"
import moment from "moment"
import pluralize from "pluralize"
import {
  getFactorCategory,
  getFactorLabel,
  getIsAggregatedFactor,
} from "helpers/factors"
import { PERFORMANCE_OVERALL_ITEMS } from "helpers/analyzeConstants"
import { RESIDUAL_ITEM } from "helpers/residualAttribution"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export function transformPerformanceData(performanceData, categories = EMPTY_ARRAY) {
  const dates = chain(performanceData)
    .first()
    .get("values.dates")
    .value() || []

  const hasTrading = some(dates, dataPoint => dataPoint?.tradingReturn)
  const hasResidual = some(dates, dataPoint => !isNil(dataPoint?.residualReturn))
  return dates.map(rawDataPoint => transformDataPoint(rawDataPoint, categories, hasTrading, hasResidual))
}

export function transformDataPoint(rawDataPoint, categories = EMPTY_ARRAY, hasTrading = false, hasResidual = false) {
  if (!rawDataPoint) { return null }
  return {
    ...omit(rawDataPoint, ["date", "totalReturn", "factorsReturn", "alphaReturn", "tradingReturn", "residualReturn"]),
    date: moment(rawDataPoint.date),
    total: rawDataPoint.totalReturn,
    factor: rawDataPoint.factorsReturn,
    alpha: rawDataPoint.alphaReturn,
    ...hasTrading && { tradingPnl: rawDataPoint.tradingReturn },
    ...hasResidual && { residual: rawDataPoint.residualReturn },
    factor_total: rawDataPoint.factorsReturn,
    ...categories.reduce((acc, category) => {
      acc[`${category.id}_total`] = rawDataPoint[category.id]
      return acc
    }, {}),
  }
}

export function createFactorItems(factorsMetadata, disabledItems = EMPTY_OBJECT, favoriteFactors = EMPTY_OBJECT) {
  if (!factorsMetadata) return EMPTY_ARRAY

  const factorsMetadataObject = chain(factorsMetadata)
    .keyBy("id")
    .value()

  return map(factorsMetadata, factor => ({
    id: factor.id,
    label: getFactorLabel(factorsMetadataObject, factor.id),
    category: getFactorCategory(factorsMetadataObject, factor.id),
    favorite: favoriteFactors[factor.id],
    field: `factors.${factor.id}`,
    disabled: !!disabledItems[factor.id],
    type: "factors",
    isAggregated: getIsAggregatedFactor(factorsMetadataObject, factor.id),
  }))
}

export function createOverallItems(categories = EMPTY_ARRAY, disabledItems = EMPTY_OBJECT, isRfrAttributionEnabled = false) {
  const overallItems = isRfrAttributionEnabled ? [...PERFORMANCE_OVERALL_ITEMS, RESIDUAL_ITEM] : PERFORMANCE_OVERALL_ITEMS
  return [
    // Overall items (total, factor, alpha, trading, residual when enabled)
    ...map(overallItems, item => ({ ...item, disabled: Boolean(disabledItems[item.id]) })),

    // Factor total item
    {
      id: "factor_total",
      color: "#9067a7",
      label: "All Factors",
      categoryLabel: "Factors",
      field: "factor",
      disabled: Boolean(disabledItems.factor_total),
      type: "category",
    },

    // Individual category items (non-selected view)
    ...chain(categories)
      .filter(category => !category.isSelectedView)
      .map(category => ({
        id: category.id,
        color: category.color,
        label: category.label,
        field: `categories.${category.id}`,
        disabled: Boolean(disabledItems[category.id]),
        type: "category",
      }))
      .value(),

    // Category total items - disabled by default, but respect explicit preferences
    ...map(categories, category => ({
      id: `${category.id}_total`,
      color: category.color,
      categoryLabel: category.isSelectedView ? category.label : pluralize(category.label),
      category: category.id,
      label: category.isSelectedView ? category.label : `All ${category.label} Factors`,
      field: `categories.${category.id}`,
      disabled: (`${category.id}_total` in disabledItems) ? Boolean(disabledItems[`${category.id}_total`]) : true,
      type: "category",
    })),
  ]
}

export function getValidCategory(selectedCategory, categories = EMPTY_ARRAY) {
  if (selectedCategory === "total" || selectedCategory === "factors") {
    return selectedCategory
  } else if (find(categories, cat => cat.id === selectedCategory)) {
    return selectedCategory
  }
  return "unknown"
}

export function getCategoryItems(factorItems, overallItems, category) {
  switch (category) {
    case "total":
      return filter(overallItems, { type: "overall" })
    case "factors":
      return filter(overallItems, ({ type, categoryLabel }) =>
        (type === "category" && !categoryLabel) // Standard category items
        || toLower(categoryLabel) === toLower(category)) // Category total item matching the selected category
    default: {
      const overall = chain(overallItems)
        .filter({ category })
        .value()

      return [
        ...overall,
        ...chain(factorItems)
          .filter({ category })
          .value(),
      ]
    }
  }
}

export function useCurrentPerformanceItems({
  categories = EMPTY_ARRAY,
  factorsMetadata = EMPTY_ARRAY,
  disabledItems = EMPTY_OBJECT,
  favoriteFactors = EMPTY_OBJECT,
  selectedCategory = "total",
  isRfrAttributionEnabled = false,
}) {
  return useMemo(() => {
    const overallItems = createOverallItems(categories, disabledItems, isRfrAttributionEnabled)
    const factorItems = createFactorItems(factorsMetadata, disabledItems, favoriteFactors)
    const validCategory = getValidCategory(selectedCategory, categories)

    return getCategoryItems(factorItems, overallItems, validCategory)
  }, [categories, factorsMetadata, disabledItems, favoriteFactors, selectedCategory, isRfrAttributionEnabled])
}
